import { useLayoutEffect, useRef } from 'react';

// Measure the actual scroll viewport, rather than assuming a header/banner height.
export default function useIncubatorDashboardLayout() {
  const rootRef = useRef(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const host = root.closest('main');
    const shell = root.closest('.inc-shell');
    const aiRow = shell?.querySelector('.inc-topline .inc-header-ai-row');
    const header = shell?.querySelector('.inc-topline');
    let frame, active = true;
    const previousShellHeight = shell?.style.getPropertyValue('--inc-dashboard-shell-height');
    const set = (element, key, value) => { if (element && element.style.getPropertyValue(key) !== value) element.style.setProperty(key, value); };
    const measure = () => {
      if (!active) return;
      const viewportBottom = window.visualViewport ? window.visualViewport.offsetTop + window.visualViewport.height : window.innerHeight;
      if (shell?.classList.contains('inc-dashboard-view')) set(shell, '--inc-dashboard-shell-height', `${Math.max(0, Math.floor(viewportBottom - shell.getBoundingClientRect().top))}px`);
      const hostRect = host?.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      const rootTop = rootRect.top + (host ? host.scrollTop : window.scrollY || 0);
      const bottom = hostRect ? Math.min(hostRect.top + host.clientHeight, viewportBottom) : viewportBottom;
      const available = Math.max(0, Math.floor(bottom - rootTop - 2));
      set(root, '--inc-dashboard-height', `${available}px`);
      const canvas = aiRow?.getBoundingClientRect();
      if (canvas && hostRect && canvas.width) {
        set(root, '--inc-dashboard-width', `${canvas.width}px`);
        set(root, '--inc-dashboard-left', `${canvas.left - hostRect.left - (host.clientLeft || 0) + host.scrollLeft}px`);
        root.dataset.aligned = 'true';
      }
      const greetingHeight = root.querySelector('.inc-dash-greeting>div')?.getBoundingClientRect().height || 0;
      const errorHeight = root.querySelector('.inc-dash-error')?.getBoundingClientRect().height || 0;
      const snapshotHeight = Math.max(128, Math.min(156, available * .18));
      const required = greetingHeight + errorHeight + snapshotHeight + 320 + 89;
      root.dataset.fit = String(window.innerWidth >= 1024 && available >= Math.max(560, required) && (canvas?.width || rootRect.width) >= 650);
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    [host, shell, shell?.parentElement, header, aiRow, root.querySelector('.inc-dash-greeting'), root.querySelector('.inc-dash-error')].filter(Boolean).forEach(element => observer?.observe(element));
    // Banner siblings may move the entire workspace without resizing the header.
    let ancestor = shell;
    while (ancestor?.parentElement && ancestor.parentElement !== document.documentElement) {
      [...ancestor.parentElement.children].filter(element => element !== ancestor).forEach(element => observer?.observe(element));
      ancestor = ancestor.parentElement;
    }
    const contentObserver = new MutationObserver(schedule);
    contentObserver.observe(root, { childList: true });
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    document.fonts?.ready.then(schedule);
    return () => {
      active = false; cancelAnimationFrame(frame); observer?.disconnect(); contentObserver.disconnect(); window.removeEventListener('resize', schedule); window.visualViewport?.removeEventListener('resize', schedule);
      if (shell) { if (previousShellHeight) shell.style.setProperty('--inc-dashboard-shell-height', previousShellHeight); else shell.style.removeProperty('--inc-dashboard-shell-height'); }
    };
  }, []);
  return rootRef;
}
